import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { resolveRedirect } from "@/lib/redirects";

/**
 * Catch-all (splat) route.
 *
 * 1. If the URL matches a known legacy path in `src/lib/redirects.ts`, we
 *    issue a permanent (301) redirect to its canonical destination so any
 *    inbound link equity is preserved.
 * 2. Otherwise we render a friendly 404 with `noindex,follow` so Google
 *    doesn't index unmatched URLs as duplicates of "/".
 */
export const Route = createFileRoute("/$")({
  beforeLoad: ({ location }) => {
    const target = resolveRedirect(location.pathname);
    if (target) {
      throw redirect({ to: target, statusCode: 301 });
    }
  },
  head: () => ({
    meta: [
      { title: "Page not found — Arif Khan" },
      { name: "robots", content: "noindex, follow" },
      { name: "description", content: "The page you're looking for doesn't exist or has been moved." },
    ],
  }),
  component: NotFoundPage,
});

function NotFoundPage() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-semibold text-foreground tracking-tight">404</h1>
        <h2 className="mt-4 text-xl font-medium text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-ink-soft">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}
